import React, { useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { Input } from "../common/Input";
import { Button } from "../common/Button";
import { Card } from "../common/Card";
import {
  GameSettings,
  SubscriptionPlan,
  PurchaseBundle,
  AdjustConfig,
} from "../../types/game.types";
import { Plus, Trash2 } from "lucide-react";

interface GameSettingsFormProps {
  settings: GameSettings;
  adjustConfig?: AdjustConfig;
  onSubmit: (
    settings: GameSettings,
    adjustConfig?: AdjustConfig
  ) => Promise<void>;
  isLoading?: boolean;
}

interface FormData {
  settings: GameSettings;
  adjustConfig: AdjustConfig;
}

export const GameSettingsForm: React.FC<GameSettingsFormProps> = ({
  settings,
  adjustConfig,
  onSubmit,
  isLoading,
}) => {
  const { control, handleSubmit, watch, setValue } = useForm<FormData>({
    defaultValues: {
      settings: {
        ...settings,
        subscriptionPlans: settings.subscriptionPlans || {
          weekly: {
            amount: 499,
            currency: "USD",
            interval: "week",
            intervalCount: 1,
            oppo: { subscribeToken: "", renewToken: "", cancelToken: "" },
            vivo: { subscribeToken: "", renewToken: "", cancelToken: "" },
          },
          monthly: {
            amount: 1499,
            currency: "USD",
            interval: "month",
            intervalCount: 1,
            oppo: { subscribeToken: "", renewToken: "", cancelToken: "" },
            vivo: { subscribeToken: "", renewToken: "", cancelToken: "" },
          },
          yearly: {
            amount: 9999,
            currency: "USD",
            interval: "year",
            intervalCount: 1,
            oppo: { subscribeToken: "", renewToken: "", cancelToken: "" },
            vivo: { subscribeToken: "", renewToken: "", cancelToken: "" },
          },
          lifetime: {
            amount: 2499,
            currency: "USD",
            oppo: { adjustToken: "" },
            vivo: { adjustToken: "" },
          },
        },
        purchaseBundles: settings.purchaseBundles || {
          starter_pack: {
            amount: 299,
            currency: "USD",
            oppo: { adjustToken: "" },
            vivo: { adjustToken: "" },
          },
          mega_pack: {
            amount: 1999,
            currency: "USD",
            oppo: { adjustToken: "" },
            vivo: { adjustToken: "" },
          },
        },
      },
      adjustConfig: adjustConfig || {
        appTokens: { default: "", oppo: "", vivo: "" },
        s2sAuthToken: "",
        subscriptionEvents: { created: "", renewed: "", cancelled: "" },
        productEvents: {},
      },
    },
  });

  const [activeTab, setActiveTab] = useState<
    "general" | "subscriptions" | "bundles" | "adjust"
  >("general");
  const [newBundleName, setNewBundleName] = useState("");

  const watchedBundles = watch("settings.purchaseBundles");

  const onFormSubmit = (data: FormData) => {
    return onSubmit(data.settings, data.adjustConfig);
  };

  const addPurchaseBundle = () => {
    if (!newBundleName.trim()) return;

    const currentBundles = watch("settings.purchaseBundles") || {};
    setValue("settings.purchaseBundles", {
      ...currentBundles,
      [newBundleName]: {
        amount: 999,
        currency: "USD",
        oppo: { adjustToken: "" },
        vivo: { adjustToken: "" },
      },
    });
    setNewBundleName("");
  };

  const removePurchaseBundle = (bundleId: string) => {
    const currentBundles = watch("settings.purchaseBundles") || {};
    const { [bundleId]: removed, ...rest } = currentBundles;
    setValue("settings.purchaseBundles", rest);
  };

  const renderSubscriptionPlan = (planType: string) => {
    const isLifetime = planType === "lifetime";

    return (
      <Card
        key={planType}
        title={`${planType.charAt(0).toUpperCase() + planType.slice(1)} Plan`}
        className="mb-4"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Controller
              name={`settings.subscriptionPlans.${planType}.amount` as any}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  type="number"
                  label="Amount (cents)"
                  placeholder="999"
                  onChange={(e) =>
                    field.onChange(parseInt(e.target.value) || 0)
                  }
                />
              )}
            />
            <Controller
              name={`settings.subscriptionPlans.${planType}.currency` as any}
              control={control}
              render={({ field }) => (
                <Input {...field} label="Currency" placeholder="USD" />
              )}
            />
          </div>

          {!isLifetime && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Controller
                  name={
                    `settings.subscriptionPlans.${planType}.interval` as any
                  }
                  control={control}
                  render={({ field }) => (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Interval
                      </label>
                      <select
                        {...field}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="week">Week</option>
                        <option value="month">Month</option>
                        <option value="year">Year</option>
                      </select>
                    </div>
                  )}
                />
                <Controller
                  name={
                    `settings.subscriptionPlans.${planType}.intervalCount` as any
                  }
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      label="Interval Count"
                      placeholder="1"
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value) || 1)
                      }
                    />
                  )}
                />
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Platform-Specific Tokens
                </h4>

                <div className="mb-3">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">
                    OPPO
                  </h5>
                  <div className="grid grid-cols-3 gap-3">
                    <Controller
                      name={
                        `settings.subscriptionPlans.${planType}.oppo.subscribeToken` as any
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="Subscribe Token"
                          placeholder="sub_token"
                        />
                      )}
                    />
                    <Controller
                      name={
                        `settings.subscriptionPlans.${planType}.oppo.renewToken` as any
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="Renew Token"
                          placeholder="renew_token"
                        />
                      )}
                    />
                    <Controller
                      name={
                        `settings.subscriptionPlans.${planType}.oppo.cancelToken` as any
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="Cancel Token"
                          placeholder="cancel_token"
                        />
                      )}
                    />
                  </div>
                </div>

                {/* VIVO Tokens */}
                <div>
                  <h5 className="text-xs font-medium text-gray-600 mb-2">
                    VIVO
                  </h5>
                  <div className="grid grid-cols-3 gap-3">
                    <Controller
                      name={
                        `settings.subscriptionPlans.${planType}.vivo.subscribeToken` as any
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="Subscribe Token"
                          placeholder="sub_token"
                        />
                      )}
                    />
                    <Controller
                      name={
                        `settings.subscriptionPlans.${planType}.vivo.renewToken` as any
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="Renew Token"
                          placeholder="renew_token"
                        />
                      )}
                    />
                    <Controller
                      name={
                        `settings.subscriptionPlans.${planType}.vivo.cancelToken` as any
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="Cancel Token"
                          placeholder="cancel_token"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {isLifetime && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Platform Adjust Tokens (One-time Purchase)
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <Controller
                  name={
                    `settings.subscriptionPlans.${planType}.oppo.adjustToken` as any
                  }
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="OPPO Adjust Token"
                      placeholder="prod_lifetime_token"
                    />
                  )}
                />
                <Controller
                  name={
                    `settings.subscriptionPlans.${planType}.vivo.adjustToken` as any
                  }
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label="VIVO Adjust Token"
                      placeholder="prod_lifetime_token"
                    />
                  )}
                />
              </div>
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {["general", "subscriptions", "bundles", "adjust"].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "general" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <Controller
              name="settings.defaultCurrency"
              control={control}
              render={({ field }) => (
                <Input {...field} label="Default Currency" placeholder="USD" />
              )}
            />
          </div>

          <Controller
            name="settings.allowedCurrencies"
            control={control}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Allowed Currencies
                </label>
                <Input
                  {...field}
                  value={
                    Array.isArray(field.value) ? field.value.join(", ") : ""
                  }
                  onChange={(e) =>
                    field.onChange(
                      e.target.value
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)
                    )
                  }
                  placeholder="USD, EUR, GBP"
                  helperText="Comma-separated list of currency codes"
                />
              </div>
            )}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Rate Limits
            </label>
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="settings.rateLimits.requests"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    label="Max Requests"
                    placeholder="100"
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 100)
                    }
                  />
                )}
              />
              <Controller
                name="settings.rateLimits.window"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="number"
                    label="Time Window (ms)"
                    placeholder="900000"
                    helperText="15 minutes = 900000ms"
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 900000)
                    }
                  />
                )}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === "subscriptions" && (
        <div className="space-y-4">
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Configure subscription plans. Weekly, Monthly, and Yearly are
              recurring subscriptions. Lifetime is a one-time purchase.
            </p>
          </div>
          {["weekly", "monthly", "yearly", "lifetime"].map(
            renderSubscriptionPlan
          )}
        </div>
      )}

      {activeTab === "bundles" && (
        <div className="space-y-4">

          {Object.entries(watchedBundles || {}).map(([bundleId, bundle]) => (
            <Card
              key={bundleId}
              title={bundleId}
              actions={
                <Button
                  type="button"
                  variant="danger"
                  size="sm"
                  onClick={() => removePurchaseBundle(bundleId)}
                  icon={<Trash2 className="w-4 h-4" />}
                >
                  Remove
                </Button>
              }
            >
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Controller
                    name={`settings.purchaseBundles.${bundleId}.amount` as any}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        label="Amount (cents)"
                        placeholder="299"
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || 0)
                        }
                      />
                    )}
                  />
                  <Controller
                    name={
                      `settings.purchaseBundles.${bundleId}.currency` as any
                    }
                    control={control}
                    render={({ field }) => (
                      <Input {...field} label="Currency" placeholder="USD" />
                    )}
                  />
                </div>

                <Controller
                  name={
                    `settings.purchaseBundles.${bundleId}.adjustToken` as any
                  }
                  control={control}
                  render={({ field }) =>
                    field.value ? (
                      <Input
                        {...field}
                        label="Adjust Token (Legacy)"
                        placeholder="prod_token"
                      />
                    ) : (
                      <></>
                    )
                  }
                />

                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    Platform Adjust Tokens
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Controller
                      name={
                        `settings.purchaseBundles.${bundleId}.oppo.adjustToken` as any
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="OPPO Adjust Token"
                          placeholder="prod_oppo_token"
                        />
                      )}
                    />
                    <Controller
                      name={
                        `settings.purchaseBundles.${bundleId}.vivo.adjustToken` as any
                      }
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="VIVO Adjust Token"
                          placeholder="prod_vivo_token"
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {(!watchedBundles || Object.keys(watchedBundles).length === 0) && (
            <Card>
              <p className="text-gray-500 text-center">
                No purchase bundles configured. Add one above.
              </p>
            </Card>
          )}
        </div>
      )}

      {activeTab === "adjust" && (
        <div className="space-y-6">
          <Card title="App Tokens">
            <div className="space-y-4">
              <Controller
                name="adjustConfig.appTokens.oppo"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="OPPO App Token"
                    placeholder="oppo_adjust_app_token"
                  />
                )}
              />
              <Controller
                name="adjustConfig.appTokens.vivo"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="VIVO App Token"
                    placeholder="vivo_adjust_app_token"
                  />
                )}
              />
            </div>
          </Card>
{/* 
          <Card title="Authentication">
            <Controller
              name="adjustConfig.s2sAuthToken"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label="S2S Auth Token"
                  type="password"
                  placeholder="your_s2s_auth_token"
                />
              )}
            />
          </Card>

          <Card title="Subscription Events">
            <div className="space-y-4">
              <Controller
                name="adjustConfig.subscriptionEvents.created"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Created Event Token"
                    placeholder="sub_created_token"
                  />
                )}
              />
              <Controller
                name="adjustConfig.subscriptionEvents.renewed"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Renewed Event Token"
                    placeholder="sub_renewed_token"
                  />
                )}
              />
              <Controller
                name="adjustConfig.subscriptionEvents.cancelled"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label="Cancelled Event Token"
                    placeholder="sub_cancelled_token"
                  />
                )}
              />
            </div>
          </Card>

          <Card title="Product Events">
            <div className="space-y-3">
              {Object.entries(watch("adjustConfig.productEvents") || {}).map(
                ([productId, token]) => (
                  <div key={productId} className="grid grid-cols-2 gap-3">
                    <Input value={productId} disabled label="Product ID" />
                    <Controller
                      name={`adjustConfig.productEvents.${productId}` as any}
                      control={control}
                      render={({ field }) => (
                        <Input
                          {...field}
                          label="Event Token"
                          placeholder="prod_event_token"
                        />
                      )}
                    />
                  </div>
                )
              )}
            </div>
          </Card> */}
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-6 border-t">
        <Button
          type="button"
          variant="secondary"
          onClick={() => window.history.back()}
        >
          Cancel
        </Button>
        <Button type="submit" isLoading={isLoading}>
          Save Settings
        </Button>
      </div>
    </form>
  );
};
